/**
 * Post-regenerate section diff panel.
 *
 * Mounts below the PaperSurface (and below the
 * ClaimAnchorWarningBanner) for any section that has a pending
 * diff in the spec store. The store captures a snapshot of the
 * section's paragraphs RIGHT BEFORE every overwrite-style
 * regenerate fires; on success the snapshot is parked in
 * ``pendingDiffBySection[sectionId]`` so this panel can render a
 * side-by-side compare + offer a one-click revert.
 *
 * Diff algorithm: pair paragraphs by ``sequence_num``; an entry
 * present only on the left side is "removed", present only on
 * the right side is "added", present on both with different text
 * is "changed". The intent is to highlight the rough shape of
 * the change — not to do a word-level diff (that's overkill for
 * an early-stage drafting tool and would muddy the visual).
 *
 * Snapshots are in-memory only — they don't survive a page
 * reload. Drafter affordances:
 *   - "Keep new version" — dismisses the snapshot (no server call).
 *   - "Revert to previous" — POSTs the snapshot back through
 *     /spec/sections/{id}/replace-paragraphs, replacing the
 *     freshly-generated paragraphs with the old ones.
 *   - "X" — same as "Keep new version" (so the drafter can
 *     dismiss without committing to a verbal label).
 */
import React, { useMemo, useState } from 'react';
import { GitCompare, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecParagraph } from '@/api/client';

// Module-level stable empty array. The Zustand selector below
// reaches for ``paragraphsBySection[sectionId]`` which is
// ``undefined`` for any section whose paragraphs haven't been
// fetched yet. Returning a fresh ``[]`` from the selector each
// call would break ``useSyncExternalStore``'s snapshot-caching
// contract: React compares snapshots with ``Object.is`` and
// when consecutive calls return new array literals it concludes
// "the snapshot is not stable" → infinite render loop → React
// error #185 ("Maximum update depth exceeded"). The same panel
// renders on every section in PaperEditor, so even one unstable
// selector multiplies into a hot loop. The stable reference
// here is the canonical Zustand-with-fallback pattern.
const _EMPTY_PARAGRAPHS: SpecParagraph[] = [];

type DiffRow =
  | {
      kind: 'same';
      sequenceNum: number;
      previous: SpecParagraph;
      current: SpecParagraph;
    }
  | {
      kind: 'changed';
      sequenceNum: number;
      previous: SpecParagraph;
      current: SpecParagraph;
    }
  | {
      kind: 'preserved';
      sequenceNum: number;
      previous: SpecParagraph;
      current: SpecParagraph;
    }
  | { kind: 'removed'; sequenceNum: number; previous: SpecParagraph }
  | { kind: 'added';   sequenceNum: number; current:  SpecParagraph };

function _normalize(text: string): string {
  // Collapse whitespace so trivial reflows ("[0001] foo" vs "[0001]
  // foo\n") don't register as a real change.
  return (text || '').replace(/\s+/g, ' ').trim();
}

function _diffParagraphs(
  previous: SpecParagraph[],
  current:  SpecParagraph[],
): DiffRow[] {
  // Key by sequence_num — the bulk-replace path preserves it,
  // and the regenerate path re-numbers fresh paragraphs starting
  // at 1. Falling back to position-pair when sequence_num
  // collides would obscure structural shifts, so we trust the
  // server-issued order.
  const prevBySeq = new Map<number, SpecParagraph>();
  previous.forEach((p) => prevBySeq.set(p.sequence_num, p));
  const currBySeq = new Map<number, SpecParagraph>();
  current.forEach((p) => currBySeq.set(p.sequence_num, p));

  const allSeqs = Array.from(
    new Set<number>([
      ...previous.map((p) => p.sequence_num),
      ...current.map((p) => p.sequence_num),
    ]),
  ).sort((a, b) => a - b);

  const rows: DiffRow[] = [];
  for (const seq of allSeqs) {
    const prev = prevBySeq.get(seq);
    const curr = currBySeq.get(seq);
    if (prev && curr) {
      const changed = _normalize(prev.body_text) !== _normalize(curr.body_text);
      // Locked paragraphs survive regenerate via the
      // ``_persist_generated_paragraphs`` lift-aside path — call
      // them out as "preserved" rather than "unchanged" so the
      // drafter sees the lock did its job. The previous-snapshot
      // and current-row share the same paragraph_id by design;
      // body_text is identical too.
      const preserved = !!curr.is_locked && !changed;
      const kind: DiffRow['kind'] =
        preserved ? 'preserved' : (changed ? 'changed' : 'same');
      rows.push({ kind, sequenceNum: seq, previous: prev, current: curr });
    } else if (prev) {
      rows.push({ kind: 'removed', sequenceNum: seq, previous: prev });
    } else if (curr) {
      rows.push({ kind: 'added',   sequenceNum: seq, current:  curr });
    }
  }
  return rows;
}

export default function SectionDiffPanel({
  sectionId,
}: {
  sectionId: string;
}): JSX.Element | null {
  const entry = useSpecStore((s) => s.pendingDiffBySection[sectionId]);
  const currentParagraphs = useSpecStore(
    // ``?? _EMPTY_PARAGRAPHS`` (module-level constant — see top
    // of file) keeps the selector snapshot referentially stable
    // when the section's paragraphs haven't been loaded yet.
    // Returning ``|| []`` here was the React #185 source — every
    // selector call manufactured a new ``[]`` literal, which
    // broke useSyncExternalStore's snapshot equality check and
    // triggered an infinite render loop across every mounted
    // SectionDiffPanel.
    (s) => s.paragraphsBySection[sectionId] ?? _EMPTY_PARAGRAPHS,
  );
  const section = useSpecStore(
    (s) => s.sections.find((sec) => sec.id === sectionId) || null,
  );
  const dismissDiff = useSpecStore((s) => s.dismissDiff);
  const revertSectionToSnapshot = useSpecStore(
    (s) => s.revertSectionToSnapshot,
  );

  const [reverting, setReverting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(
    () => (entry ? _diffParagraphs(entry.previous, currentParagraphs) : []),
    [entry, currentParagraphs],
  );

  if (!entry) return null;
  if (section?.is_frozen) return null;

  const counts = rows.reduce(
    (acc, r) => {
      if (r.kind === 'changed') acc.changed += 1;
      else if (r.kind === 'removed') acc.removed += 1;
      else if (r.kind === 'added') acc.added += 1;
      else if (r.kind === 'preserved') acc.preserved += 1;
      return acc;
    },
    { changed: 0, removed: 0, added: 0, preserved: 0 },
  );
  const hasAnyChange =
    counts.changed > 0 || counts.removed > 0 || counts.added > 0
    || counts.preserved > 0;
  const summary = !hasAnyChange
    ? 'No paragraph-level differences detected.'
    : [
        counts.added     > 0 ? `${counts.added} added`         : null,
        counts.removed   > 0 ? `${counts.removed} removed`     : null,
        counts.changed   > 0 ? `${counts.changed} changed`     : null,
        counts.preserved > 0 ? `${counts.preserved} preserved` : null,
      ].filter(Boolean).join(' · ');

  const onRevert = async () => {
    if (reverting) return;
    setReverting(true);
    try {
      await revertSectionToSnapshot(sectionId);
    } finally {
      setReverting(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-sm border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-zinc-800"
      role="region"
      aria-label="Section regenerate diff"
      data-testid={`spec-section-diff-${sectionId}`}
    >
      <div className="flex items-start gap-2">
        <GitCompare
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600"
          aria-hidden
        />
        <div className="flex-1 space-y-1">
          <div className="font-medium text-sky-900">
            Section regenerated
            <span className="ml-1 font-normal text-sky-800">
              · {summary}
            </span>
          </div>
          {/* Item 4 — every regenerate surfaces the steering
              mode that drove it. ``steering=null`` means the
              drafter ran a plain regenerate (or explicitly
              chose "Ignore steering" in the choice dialog); the
              "without steering" line makes that visible so the
              drafter knows the output isn't responding to the
              steering text they may have left in the panel. */}
          {entry.steering ? (
            <div
              className="text-zinc-700"
              data-testid={`spec-section-diff-steering-${sectionId}`}
            >
              Regenerated with:{' '}
              <span className="italic">&ldquo;{entry.steering}&rdquo;</span>
            </div>
          ) : (
            <div
              className="text-zinc-500 italic"
              data-testid={`spec-section-diff-no-steering-${sectionId}`}
            >
              Regenerated without steering.
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-sm border border-sky-400 bg-white px-2 py-0.5 text-sky-900 hover:bg-sky-100 disabled:opacity-50"
              onClick={onRevert}
              disabled={reverting || entry.previous.length === 0}
              data-testid={`spec-section-diff-revert-${sectionId}`}
            >
              {reverting ? 'Reverting…' : 'Revert to previous'}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 py-0.5 text-zinc-700 hover:bg-zinc-100"
              onClick={() => dismissDiff(sectionId)}
              data-testid={`spec-section-diff-keep-${sectionId}`}
            >
              Keep new version
            </button>
            {hasAnyChange && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 text-sky-800 underline-offset-2 hover:underline"
                onClick={() => setExpanded((v) => !v)}
                data-testid={`spec-section-diff-toggle-${sectionId}`}
              >
                {expanded ? 'Hide diff' : 'Show diff'}
              </button>
            )}
          </div>
          {expanded && hasAnyChange && (
            <ul
              className="mt-2 max-h-[420px] space-y-2 overflow-y-auto pr-1"
              data-testid={`spec-section-diff-list-${sectionId}`}
            >
              {rows.map((row, idx) => (
                <DiffRowView key={`${row.kind}-${row.sequenceNum}-${idx}`} row={row} />
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-400 hover:bg-sky-100 hover:text-zinc-700"
          onClick={() => dismissDiff(sectionId)}
          aria-label="Dismiss section regenerate diff"
          data-testid={`spec-section-diff-dismiss-${sectionId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function DiffRowView({ row }: { row: DiffRow }): JSX.Element {
  if (row.kind === 'same') {
    return (
      <li className="rounded-sm border border-zinc-200 bg-white px-2 py-1 text-zinc-500">
        <div className="text-[10px] uppercase tracking-wide text-zinc-400">
          ¶ {row.sequenceNum} · unchanged
        </div>
        <div className="line-clamp-1">{row.current.body_text}</div>
      </li>
    );
  }
  if (row.kind === 'preserved') {
    return (
      <li className="rounded-sm border-l-2 border-amber-400 border-y border-r border-zinc-200 bg-amber-50/50 px-2 py-1 text-zinc-700">
        <div className="text-[10px] uppercase tracking-wide text-amber-700">
          ¶ {row.sequenceNum} · preserved (locked)
        </div>
        <div className="line-clamp-1">{row.current.body_text}</div>
      </li>
    );
  }
  if (row.kind === 'added') {
    return (
      <li className="rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-emerald-900">
        <div className="text-[10px] uppercase tracking-wide text-emerald-700">
          ¶ {row.sequenceNum} · added
        </div>
        <div className="whitespace-pre-wrap">{row.current.body_text}</div>
      </li>
    );
  }
  if (row.kind === 'removed') {
    return (
      <li className="rounded-sm border border-rose-300 bg-rose-50 px-2 py-1 text-rose-900">
        <div className="text-[10px] uppercase tracking-wide text-rose-700">
          ¶ {row.sequenceNum} · removed
        </div>
        <div className="whitespace-pre-wrap line-through opacity-80">
          {row.previous.body_text}
        </div>
      </li>
    );
  }
  // changed: stacked before/after
  return (
    <li className="rounded-sm border border-amber-300 bg-amber-50 px-2 py-1 text-zinc-800">
      <div className="text-[10px] uppercase tracking-wide text-amber-800">
        ¶ {row.sequenceNum} · changed
      </div>
      <div className="mt-1 rounded-sm border border-rose-200 bg-white px-1 py-0.5 text-rose-900">
        <div className="text-[10px] uppercase tracking-wide text-rose-600">
          Previous
        </div>
        <div className="whitespace-pre-wrap">{row.previous.body_text}</div>
      </div>
      <div className="mt-1 rounded-sm border border-emerald-200 bg-white px-1 py-0.5 text-emerald-900">
        <div className="text-[10px] uppercase tracking-wide text-emerald-600">
          New
        </div>
        <div className="whitespace-pre-wrap">{row.current.body_text}</div>
      </div>
    </li>
  );
}
